import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { useHealthlinkPracticeSave } from "features/healthlink/hooks/useHealthlinkPracticeSave";
import { onInvalidSubmit } from "utils/reportFormErrors";

import style from "./HealthlinkPracticeForm.module.css";

const formSchema = z.object({
  practiceId: z.string().min(1, "Practice ID is required"),
  password: z.string().min(1, "Password is required"),
  certificate: z.any().refine((value) => {
    if (!value || !value[0]) return false;
    const file = value[0];
    return (
      file instanceof File &&
      (file.type === "application/x-pkcs12" || file.name.endsWith(".pfx"))
    );
  }, "Only PFX files are allowed"),
});

export const HealthlinkPracticeForm = ({ closeModal, initialValues }) => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: initialValues?.password || "",
      practiceId: initialValues?.practiceId || "",
      certificate: null,
    },
  });

  const saveCredentials = useHealthlinkPracticeSave();

  const handleSubmit = async (values) => {
    try {
      const file = values.certificate?.[0];
      if (!file) {
        throw new Error("No certificate file provided");
      }

      const formData = new FormData();
      formData.append("certificate", file);

      await saveCredentials.mutateAsync({
        formData,
        practiceId: values.practiceId,
        password: values.password,
      });
      form.reset();
      closeModal();
    } catch (error) {
      toast.error("Something went wrong");
      console.error("Error processing form submission:", error);
    }
  };

  return (
    <>
      <FormProvider {...form}>
        <form
          className={style.addLetterForm}
          onSubmit={form.handleSubmit(handleSubmit, onInvalidSubmit("HealthlinkPracticeForm"))}
        >
          <FormField name="practiceId">
            <label className={style.addLetterFormLabel}>Practice ID</label>
            <div>
              <input
                type="text"
                className="form-control"
                {...form.register("practiceId")}
              />
            </div>
          </FormField>

          <FormField name="password">
            <label className={style.addLetterFormLabel}>Password</label>
            <div>
              <input
                type="text"
                className="form-control"
                {...form.register("password")}
              />
            </div>
          </FormField>

          <FormField name="certificate">
            <label className={style.addLetterFormLabel}>Certificate</label>
            <div>
              <input
                type="file"
                className="form-control"
                accept=".pfx"
                style={{ paddingLeft: "18px" }}
                {...form.register("certificate")}
              />
            </div>
          </FormField>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              disabled={saveCredentials.isPending}
              className={style.sendButton}
            >
              {saveCredentials.isPending ? "Submitting..." : "Save"}
            </button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
